import { type ActionReducerMapBuilder, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import type { TagData } from 'src/helper/shared-types';
import { processTrips, resolveIssueMaterials, sortByCreatedAt } from 'src/helper/work-order-utils';
import { applyGroupedDataPage, applyGroupFetchRejected } from 'src/redux/common/reducers';
import {
  addWorkOrderProblemCategory,
  closeTrip,
  coordinatorQuoteApprove,
  coordinatorQuoteReject,
  createQuote,
  createQuoteTask,
  deleteTag,
  deleteWorkOrderDueDate,
  deleteWorkOrderMedia,
  dispatchToLula,
  editQuote,
  getAllTags,
  getCloseTripInfo,
  getIssueDetailsForCloseTrip,
  getOwnerQuoteDetails,
  getQuoteDetails,
  getScheduleContext,
  getScheduleContextTechnicians,
  getTechnicianTimeSlots,
  getVendorAvailability,
  getWorkOrderDetails,
  getWorkOrders,
  getWorkOrdersForGroup,
  markAsDoneIssue,
  markAsPendingIssue,
  ownerQuoteApprove,
  ownerQuoteReject,
  reScheduleWorkOrder,
  rescheduleWorkOrderTrip,
  restoreQuote,
  revalidateOwnerQuoteDetails,
  scheduleToVendor,
  scheduleWorkOrder,
  sendForCustomerApproval,
  updateDriveOrLaborTimeInCloseTrip,
  updateQuoteTask,
  updateVendorLaborCloseTrip,
  updateWorkOrderAccessMethod,
  updateWorkOrderDescription,
  updateWorkOrderDueDate,
  updateWorkOrderNteAmount,
  updateWorkOrderPriority,
  updateWorkOrderProblemCategory,
  updateWorkOrderResidentInfo,
  updateWorkOrderToVendor,
} from 'src/redux/work-order/api';
import type {
  CloseTripIssueObjType,
  GetIssueDetailsApiResType,
  InitialStateType,
  InvoiceDataObj,
  QuoteDetailsObj,
  SendForCustomerApprovalResponse,
  WorkOrder,
  WorkOrderAPIResponseObj,
  WorkOrderDataObj,
} from 'src/redux/work-order/types';
import {
  calculateMaterialTotal,
  formatResponseForQuoteSource,
} from 'src/views/WorkOrder/SidePanel/Quotes/quoteUtils';
import type { MaterialObjTypeForApi } from 'src/views/WorkOrder/SidePanel/Trips/CloseTrip/type';
import type { Issue } from '../issues/types';
import { getServiceRequestDetails } from '../service-request/api';

// Normalize a work order coming from a listing endpoint into the shape the
// `source` map expects: `tags` collapsed to an array of tag_ids (with the full
// tag objects hoisted into `tagSource`), trips/quotes/invoices reshaped, and
// resident availabilities indexed by date. Shared by the flat listing
// (`getWorkOrders`) and the per-group listing (`getWorkOrdersForGroup`) so both
// paths produce identical, component-ready source entries.
const normalizeWorkOrderListItem = (
  item: WorkOrderAPIResponseObj,
  tagSource: { [key: string]: TagData },
  existingSourceItem?: WorkOrderDataObj,
): WorkOrderDataObj => {
  const normalized = {
    ...item,
    service_request: {} as WorkOrderDataObj['service_request'],
    work_order_issues: [] as WorkOrderDataObj['work_order_issues'],
    tags: item?.tags?.map((tag) => {
      tagSource[tag?.tag_id] = tag;
      return tag?.tag_id;
    }),
    trips: processTrips(item?.trips || []),
    quotes: {
      quoteIds: item?.quotes?.map((quote) => quote?.quote_id),
      quoteSource: item?.quotes?.reduce((acc: { [key: string]: QuoteDetailsObj }, quote) => {
        acc[quote?.quote_id] = quote;
        return acc;
      }, {}),
    },
    resident_availabilities: {
      ...(item?.resident_availabilities || {}),
      availability: item?.resident_availabilities?.availability,
    },
    invoices: {
      invoiceIds: item?.invoices?.map((invoice) => invoice?.invoice_id),
      invoiceSource: item?.invoices?.reduce<Record<string, InvoiceDataObj>>(
        (acc: Record<string, InvoiceDataObj>, invoice: InvoiceDataObj) => {
          acc[invoice?.invoice_id] = {
            ...(existingSourceItem?.invoices?.invoiceSource?.[invoice?.invoice_id] || {}),
            ...(invoice || {}),
          };
          return acc;
        },
        {},
      ),
    },
  } as unknown as WorkOrderDataObj;

  const availableDates =
    [] as WorkOrderAPIResponseObj['resident_availabilities']['available_dates'];
  const residentAvailabilitySource =
    {} as WorkOrderAPIResponseObj['resident_availabilities']['resident_availability_source'];
  item?.resident_availabilities?.availability?.forEach((availability) => {
    if (availability.date) {
      availableDates?.push(availability.date);
      if (residentAvailabilitySource) {
        residentAvailabilitySource[availability.date] = availability;
      }
    }
  });

  normalized.resident_availabilities = {
    ...(normalized.resident_availabilities || {}),
    available_dates: availableDates,
    resident_availability_source: residentAvailabilitySource,
  };

  return normalized;
};

const extraReducers = (builder: ActionReducerMapBuilder<InitialStateType>) => {
  // users list page filter options
  builder.addCase(getWorkOrders().pending, (state: InitialStateType, action) => {
    if (!action.meta.arg.work_order_id) {
      state.isLoading = true;
      state.currentFilterReferenceObj = action.meta.arg;
    }
  });
  builder.addCase(getWorkOrders().fulfilled, (state: InitialStateType, action) => {
    // Which WO keeps its rich detail record is determined by the thunk arg captured
    // at dispatch time — NOT by reading `window` at completion time. A background
    // (e.g. Pusher) refetch that resolves after the panel has switched must still
    // protect the WO it was dispatched for (COM-715). Falls back to the open panel
    // for callers that don't pass an id.
    const expandedWorkOrderId =
      action?.meta?.arg?.expandedWorkOrderId || state?.showSidePanel?.workOrderUuid || '';
    const newSource = { ...(state?.source || {}) };
    const newTagSource = { ...(state?.tagSource || {}) };

    const newWoIds = [...(Number(action.meta.arg.page || 0) > 1 ? state?.data?.data || [] : [])];
    action?.payload?.data?.forEach((item: WorkOrderAPIResponseObj) => {
      // checking the new wo id is already exist in previous list.
      if (item?.work_order_id && newWoIds?.includes(item?.work_order_id)) {
        newWoIds.splice(newWoIds.indexOf(item?.work_order_id), 1);
      }
      newWoIds.push(item?.work_order_id);
      if (
        item?.work_order_id !== expandedWorkOrderId ||
        isEmpty(state?.source?.[expandedWorkOrderId])
      ) {
        newSource[item?.work_order_id] = normalizeWorkOrderListItem(
          item,
          newTagSource,
          state?.source?.[item?.work_order_id],
        );
      }
    });
    // update the list only when work_order_id not exist in action.meta.arg
    // if the work_order_id contains in the argument, it means the api called due to pusher update.
    if (!action.meta.arg.work_order_id) {
      state.data = {
        data: newWoIds,
        meta: action?.payload?.meta || {},
      };
      state.isLoading = false;
    }
    state.tagSource = newTagSource;
    state.source = newSource;
  });
  builder.addCase(getWorkOrders().rejected, (state: InitialStateType, action) => {
    if (!action.meta.arg.work_order_id) {
      state.isLoading = false;
    }
  });

  // Fetch Work order details
  builder.addCase(getWorkOrderDetails().pending, (state: InitialStateType) => {
    state.hasWorkOrderDetailsError = false;
    if (!state?.showSidePanel?.needToRefetch) {
      state.isFetchingWorkOrderDetails = true;
    }
  });
  builder.addCase(getWorkOrderDetails().fulfilled, (state: InitialStateType, action) => {
    const workOrderData: WorkOrderAPIResponseObj = action?.payload || {};
    const currentData = state.source[workOrderData?.work_order_id];
    const newTagSource = { ...(state?.tagSource || {}) };

    const isFieldsNotEditable = action?.payload?.is_scheduled_for_lula;

    state.source[workOrderData?.work_order_id] = {
      ...currentData,
      ...workOrderData,
      tags: workOrderData?.tags?.map((tag: TagData) => {
        newTagSource[tag?.tag_id] = tag;
        return tag?.tag_id;
      }),
      service_request: {
        // used to show the work order list dropdown on the top of the work order details page
        serviceRequestId: workOrderData?.service_request?.service_request_id || '',
        serviceRequestNumber: workOrderData?.service_request?.service_request_number || '',
        status: workOrderData?.service_request?.status || {},
        // list of work orders that are related to this service request of this work order
        workOrderIds:
          workOrderData?.service_request?.work_orders?.map(({ work_order_id }) => work_order_id) ||
          [],

        workOrders:
          workOrderData?.service_request?.work_orders?.reduce<Record<string, WorkOrder>>(
            (acc: Record<string, WorkOrder>, workOrder: WorkOrder) => {
              acc[workOrder.work_order_id] = workOrder;
              return acc;
            },
            {},
          ) || {},
      },
      work_order_issues: sortByCreatedAt(workOrderData?.work_order_issues || []).map(
        (issue: Issue) => issue?.issue_id,
      ),
      quotes: {
        quoteIds: workOrderData?.quotes?.map((quote) => quote?.quote_id),
        quoteSource: workOrderData?.quotes?.reduce<Record<string, QuoteDetailsObj>>(
          (acc: Record<string, QuoteDetailsObj>, quote: QuoteDetailsObj) => {
            acc[quote?.quote_id] = {
              ...(state?.source?.[workOrderData?.work_order_id]?.quotes?.quoteSource?.[
                quote?.quote_id
              ] || {}),
              ...(quote || {}),
            };
            return acc;
          },
          {},
        ),
      },
      trips: processTrips(workOrderData?.trips || []),
      resident_availabilities: {
        ...(workOrderData?.resident_availabilities || {}),
        availability: workOrderData?.resident_availabilities?.availability,
      },
      invoices: {
        invoiceIds: workOrderData?.invoices?.map((invoice) => invoice?.invoice_id),
        invoiceSource: workOrderData?.invoices?.reduce<Record<string, InvoiceDataObj>>(
          (acc: Record<string, InvoiceDataObj>, invoice: InvoiceDataObj) => {
            acc[invoice?.invoice_id] = {
              ...(state?.source?.[workOrderData?.work_order_id]?.invoices?.invoiceSource?.[
                invoice?.invoice_id
              ] || {}),
              ...(invoice || {}),
            };
            return acc;
          },
          {},
        ),
      },
      nonEditableFields: isFieldsNotEditable
        ? [
            'address',
            'priority',
            'resident_info',
            'nte',
            'media',
            'issues',
            'resident_availability',
            'access_info',
          ]
        : [],
    };

    const availableDates =
      [] as WorkOrderAPIResponseObj['resident_availabilities']['available_dates'];

    const residentAvailabilitySource =
      {} as WorkOrderAPIResponseObj['resident_availabilities']['resident_availability_source'];

    workOrderData?.resident_availabilities?.availability?.forEach((availability) => {
      if (availability.date) {
        availableDates?.push(availability.date);
        if (residentAvailabilitySource) {
          residentAvailabilitySource[availability.date] = availability;
        }
      }
    });

    state.source[workOrderData?.work_order_id].resident_availabilities = {
      ...(state.source[workOrderData?.work_order_id].resident_availabilities || {}),
      available_dates: [],
      resident_availability_source: {},
    };

    state.source[workOrderData?.work_order_id].resident_availabilities.available_dates =
      availableDates;
    state.source[
      workOrderData?.work_order_id
    ].resident_availabilities.resident_availability_source = residentAvailabilitySource;

    // Server truth has replaced `resident_availabilities` above, so there is no
    // optimistic stamp left to settle or roll back (APX-607).
    for (const [serviceRequestId, pendingRequest] of Object.entries(
      state.pendingAvailabilityRequests || {},
    )) {
      if (pendingRequest?.workOrderId === workOrderData?.work_order_id) {
        delete state.pendingAvailabilityRequests[serviceRequestId];
      }
    }

    state.isFetchingWorkOrderDetails = false;
    state.hasWorkOrderDetailsError = false;
    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      needToRefetch: false,
    };
    state.tagSource = newTagSource;
  });
  builder.addCase(getWorkOrderDetails().rejected, (state: InitialStateType, action) => {
    // A canceled rejection belongs to a request that was superseded by a newer in-flight
    // one; flipping these shared flags here clobbers the successor's loading state and
    // drops the refetch it was told to perform. Mirrors list-view/extra-reducers.ts:26-31.
    if ((action.payload as { actionType?: string })?.actionType === 'canceled') return;
    state.isFetchingWorkOrderDetails = false;
    // Only a genuine load failure lights the error state — a canceled/aborted request (WO
    // switch, unmount) or the 401 sign-out shape must not surface as "couldn't load".
    // Same taxonomy as `isCanceledError` and the SR panel's rejected reducer.
    const actionType = (action.payload as { actionType?: string } | undefined)?.actionType;
    state.hasWorkOrderDetailsError = actionType !== 'canceled' && actionType !== 'unauthorized';
    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      needToRefetch: false,
    };
  });

  // update work order description
  builder.addCase(updateWorkOrderDescription().pending, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = true;
  });
  builder.addCase(updateWorkOrderDescription().fulfilled, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });
  builder.addCase(updateWorkOrderDescription().rejected, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });

  // update work order accessMethod
  builder.addCase(updateWorkOrderAccessMethod().pending, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = true;
  });
  builder.addCase(updateWorkOrderAccessMethod().fulfilled, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });
  builder.addCase(updateWorkOrderAccessMethod().rejected, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });

  // update work order due date
  builder.addCase(updateWorkOrderDueDate().pending, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = true;
  });
  builder.addCase(updateWorkOrderDueDate().fulfilled, (state: InitialStateType, action) => {
    const currentData = state.source;
    const workOrderToUpdate = state.source[action?.payload?.work_order_id];
    const updatedWorkOrder = {
      ...workOrderToUpdate,
      due_date: action?.payload?.due_date,
    };
    state.source = { ...currentData, [action?.payload?.work_order_id]: updatedWorkOrder };
    state.isUpdatingWorkOrderDetails = false;
  });
  builder.addCase(updateWorkOrderDueDate().rejected, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });

  // delete work order due date
  builder.addCase(deleteWorkOrderDueDate().pending, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = true;
  });

  builder.addCase(deleteWorkOrderDueDate().fulfilled, (state: InitialStateType, action) => {
    const currentData = state.source;
    const workOrderToUpdate = state.source[action?.payload?.work_order_id];
    const updatedWorkOrder = {
      ...workOrderToUpdate,
      due_date: null, // Set to null when deleted
    };
    state.source = { ...currentData, [action?.payload?.work_order_id]: updatedWorkOrder };
    state.isUpdatingWorkOrderDetails = false;
  });
  builder.addCase(deleteWorkOrderDueDate().rejected, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });

  // update work order problem category
  builder.addCase(updateWorkOrderProblemCategory().pending, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });
  builder.addCase(updateWorkOrderProblemCategory().fulfilled, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = true;
  });
  builder.addCase(updateWorkOrderProblemCategory().rejected, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });
  // update work order problem category
  builder.addCase(addWorkOrderProblemCategory().pending, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = true;
  });
  builder.addCase(addWorkOrderProblemCategory().fulfilled, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });
  builder.addCase(addWorkOrderProblemCategory().rejected, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });
  // update work order nte amount
  builder.addCase(updateWorkOrderNteAmount().pending, (state: InitialStateType, action) => {
    state.isUpdatingWorkOrderDetails = true;
    // Immediately update Redux state for instant UI feedback
    const arg = action?.meta?.arg || {};
    // pending fires synchronously at click-time within the open panel, so the
    // side-panel id is still the WO being edited here (unlike rejected — COM-715).
    const workOrderId = state?.showSidePanel?.workOrderUuid || '';
    const currentData = { ...(state?.source || {}) };
    const currentWorkOrderNteData = { ...(state?.source?.[workOrderId]?.nte_amount || {}) };
    const isAiRecommended =
      currentWorkOrderNteData?.is_ai_recommended &&
      arg?.nte_amount_in_cents === currentWorkOrderNteData?.nte_amount_in_cents;
    const updatedWorkOrder = {
      ...(state?.source?.[workOrderId] || {}),
      nte_amount: {
        ...currentWorkOrderNteData,
        nte_amount_in_cents: arg?.nte_amount_in_cents,
        is_ai_recommended: isAiRecommended,
      },
    };
    state.source = { ...currentData, [workOrderId]: updatedWorkOrder };
  });
  builder.addCase(updateWorkOrderNteAmount().fulfilled, (state: InitialStateType, action) => {
    state.isUpdatingWorkOrderDetails = false;
    // update ai recommendation on success
    const workOrderId = action?.payload?.work_order_id || action?.payload?.workOrderId || '';
    const currentData = { ...(state?.source || {}) };
    const currentWorkOrderNteData = { ...(state?.source?.[workOrderId]?.nte_amount || {}) };
    const updatedWorkOrder = {
      ...(state?.source?.[workOrderId] || {}),
      nte_amount: {
        ...currentWorkOrderNteData,
        is_ai_recommended: action?.payload?.is_ai_recommended || false,
      },
    };
    state.source = { ...currentData, [workOrderId]: updatedWorkOrder };
  });
  builder.addCase(updateWorkOrderNteAmount().rejected, (state: InitialStateType, action: any) => {
    state.isUpdatingWorkOrderDetails = false;
    // Revert to original value on error. Key off the WO the request was for
    // (carried in the payload), NOT the currently-open side panel — the panel may
    // have switched while the PUT was in flight, and rolling back onto the new WO
    // would corrupt its NTE while leaving the original with an unconfirmed value (COM-715).
    const workOrderId = action?.payload?.workOrderId || '';
    const currentData = { ...(state?.source || {}) };
    const currentWorkOrderNteData = { ...(state?.source?.[workOrderId]?.nte_amount || {}) };
    const updatedWorkOrder = {
      ...(state?.source?.[workOrderId] || {}),
      nte_amount: {
        ...currentWorkOrderNteData,
        nte_amount_in_cents: action?.payload?.prevNteAmount,
        is_ai_recommended: action?.payload?.prevIsAiRecommended,
      },
    };
    state.source = { ...currentData, [workOrderId]: updatedWorkOrder };
  });

  // update work order residentInfo
  builder.addCase(updateWorkOrderResidentInfo().pending, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = true;
  });
  builder.addCase(updateWorkOrderResidentInfo().fulfilled, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });
  builder.addCase(updateWorkOrderResidentInfo().rejected, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });

  // update priority
  builder.addCase(updateWorkOrderPriority().pending, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = true;
  });
  builder.addCase(updateWorkOrderPriority().fulfilled, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });
  builder.addCase(updateWorkOrderPriority().rejected, (state: InitialStateType) => {
    state.isUpdatingWorkOrderDetails = false;
  });

  builder.addCase(
    getScheduleContext().rejected,
    (state: InitialStateType, action: PayloadAction<any>) => {
      const workOrderId = action?.payload?.workOrderId;
      if (!isEmpty(state?.source?.[workOrderId]?.schedule_details)) {
        state.source[workOrderId].schedule_details = {} as WorkOrderDataObj['schedule_details'];
      }
      state.isFetchingTechnicianForSchedule = false;
    },
  );
  // get technicians for schedule
  builder.addCase(getScheduleContextTechnicians().pending, (state: InitialStateType) => {
    state.isFetchingTechnicianForSchedule = true;
  });
  builder.addCase(
    getScheduleContextTechnicians().fulfilled,
    (state: InitialStateType, action: any) => {
      const { workOrderId, ...res } = action.payload;
      state.source[workOrderId].schedule_details.provider_settings.technician.availableTechnicians =
        res;
      state.isFetchingTechnicianForSchedule = false;
    },
  );
  builder.addCase(getScheduleContextTechnicians().rejected, (state: InitialStateType) => {
    state.isFetchingTechnicianForSchedule = false;
  });

  // get technician slots for schedule
  builder.addCase(getTechnicianTimeSlots().pending, (state: InitialStateType) => {
    state.isFetchingTechnicianSlotsForSchedule = true;
  });
  builder.addCase(getTechnicianTimeSlots().fulfilled, (state: InitialStateType) => {
    state.isFetchingTechnicianSlotsForSchedule = false;
  });
  builder.addCase(getTechnicianTimeSlots().rejected, (state: InitialStateType) => {
    state.isFetchingTechnicianSlotsForSchedule = false;
  });

  // get vendor slots for schedule
  builder.addCase(getVendorAvailability().pending, (state: InitialStateType) => {
    state.isFetchingVendorSlotsForSchedule = true;
  });
  builder.addCase(getVendorAvailability().fulfilled, (state: InitialStateType) => {
    state.isFetchingVendorSlotsForSchedule = false;
  });
  builder.addCase(getVendorAvailability().rejected, (state: InitialStateType) => {
    state.isFetchingVendorSlotsForSchedule = false;
  });

  // schedule work order
  builder.addCase(scheduleWorkOrder().pending, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = true;
  });
  builder.addCase(scheduleWorkOrder().fulfilled, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });
  builder.addCase(scheduleWorkOrder().rejected, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });

  // re-schedule work order trip
  builder.addCase(rescheduleWorkOrderTrip().pending, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = true;
  });
  builder.addCase(rescheduleWorkOrderTrip().fulfilled, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });
  builder.addCase(rescheduleWorkOrderTrip().rejected, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });

  // schedule work order to lula/vendor
  builder.addCase(scheduleToVendor().pending, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = true;
  });
  builder.addCase(scheduleToVendor().fulfilled, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });
  builder.addCase(scheduleToVendor().rejected, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });

  // update schedule work order to vendor
  builder.addCase(updateWorkOrderToVendor().pending, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = true;
  });
  builder.addCase(updateWorkOrderToVendor().fulfilled, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });
  builder.addCase(updateWorkOrderToVendor().rejected, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });

  // re schedule work order
  builder.addCase(reScheduleWorkOrder().pending, (state: InitialStateType) => {
    state.isReSchedulingWorkOrder = true;
  });
  builder.addCase(reScheduleWorkOrder().fulfilled, (state: InitialStateType) => {
    state.isReSchedulingWorkOrder = false;
  });
  builder.addCase(reScheduleWorkOrder().rejected, (state: InitialStateType) => {
    state.isReSchedulingWorkOrder = false;
  });

  //  get tags
  builder.addCase(getAllTags().pending, (state: InitialStateType) => {
    state.isFetchingTags = true;
  });
  builder.addCase(getAllTags().fulfilled, (state: InitialStateType, action) => {
    const newSource = { ...(state.tagSource || {}) };
    const newTagIds = [
      ...(Number(action.meta.arg.page || 0) > 1 ? state?.tagData?.data || [] : []),
    ];
    action?.payload?.data?.forEach((item: TagData) => {
      // checking the new tag id is already exist in previous list.
      if (item?.tag_id && newTagIds?.includes(item?.tag_id)) {
        newTagIds.splice(newTagIds.indexOf(item?.tag_id), 1);
      }
      newTagIds.push(item?.tag_id);
      newSource[item?.tag_id] = {
        ...item,
      };
    });
    state.tagData = {
      data: newTagIds,
      meta: action?.payload?.meta || {},
    };
    state.tagSource = newSource;
    state.isFetchingTags = false;
  });
  builder.addCase(getAllTags().rejected, (state: InitialStateType) => {
    state.isFetchingTags = false;
  });
  builder.addCase(deleteTag().fulfilled, (state: InitialStateType, action) => {
    const newSource = { ...(state || {}) };
    delete newSource?.tagSource?.[action.payload?.tag_id];
    newSource.tagData.data = (newSource?.tagData?.data || [])?.filter(
      (id) => id !== action?.payload?.tag_id,
    );
    state = newSource;
  });
  builder.addCase(createQuote().pending, (state: InitialStateType) => {
    state.isCreatingQuote = true;
  });
  builder.addCase(createQuote().fulfilled, (state: InitialStateType) => {
    state.isCreatingQuote = false;
  });
  builder.addCase(createQuote().rejected, (state: InitialStateType) => {
    state.isCreatingQuote = false;
  });
  // get owner quote details
  builder.addCase(getQuoteDetails().pending, (state: InitialStateType) => {
    if (!state?.shouldRefetchQuote) {
      state.isFetchingQuoteDetails = true;
    }
  });
  builder.addCase(getQuoteDetails().fulfilled, (state: InitialStateType, action) => {
    state.isFetchingQuoteDetails = false;
    const woId = action?.payload?.work_order_id;
    const quoteId = action?.payload?.quote_id;
    if (state?.source?.[woId]) {
      if (quoteId) {
        // When Pusher adds a new wo it will not have quotes object initially
        // so  ensure quotes object exists
        if (!state.source[woId].quotes) {
          state.source[woId].quotes = { quoteSource: {}, quoteIds: [] };
        }
        if (!state.source[woId].quotes.quoteSource) {
          state.source[woId].quotes.quoteSource = {};
        }
        if (!state.source[woId].quotes.quoteIds) {
          state.source[woId].quotes.quoteIds = [];
        }

        state.source[woId].quotes.quoteSource[quoteId] = {
          ...(state?.source?.[woId]?.quotes?.quoteSource?.[quoteId] || {}),
          ...(formatResponseForQuoteSource(action?.payload) || {}),
        };
      }
    } else {
      state.source[woId] = {
        ...(state?.source?.[woId] || {}),
        quotes: {
          ...(state?.source?.[woId]?.quotes || {}),
          quoteSource: {
            ...(state?.source?.[woId]?.quotes?.quoteSource || {}),
            [quoteId]: {
              ...(state?.source?.[woId]?.quotes?.quoteSource?.[quoteId] || {}),
              ...(formatResponseForQuoteSource(action?.payload) || {}),
            },
          },
          quoteIds: !state?.source?.[woId]?.quotes?.quoteIds?.includes(quoteId)
            ? [...(state?.source?.[woId]?.quotes?.quoteIds || []), quoteId]
            : state?.source?.[woId]?.quotes?.quoteIds,
        },
      };
    }
    if (state?.shouldRefetchQuote) {
      state.shouldRefetchQuote = false;
    }
  });
  builder.addCase(getQuoteDetails().rejected, (state: InitialStateType) => {
    state.isFetchingQuoteDetails = false;
    if (state?.shouldRefetchQuote) {
      state.shouldRefetchQuote = false;
    }
  });

  builder.addCase(getOwnerQuoteDetails().pending, (state: InitialStateType) => {
    state.isFetchingQuoteDetails = true;
  });
  builder.addCase(getOwnerQuoteDetails().fulfilled, (state: InitialStateType, action) => {
    state.ownerQuote.quoteDetails = formatResponseForQuoteSource(action.payload);
    state.isFetchingQuoteDetails = false;
  });
  builder.addCase(getOwnerQuoteDetails().rejected, (state: InitialStateType) => {
    state.isFetchingQuoteDetails = false;
  });

  // Background revalidation of an owner quote the visitor is already looking at.
  //
  // Deliberately touches NEITHER `isFetchingQuoteDetails` (which would collapse
  // the page into a skeleton and remount the form, wiping the owner's
  // selections) NOR any error state. There is no `pending` case at all, and
  // `rejected` is a no-op on purpose: a revalidation that fails must leave the
  // owner looking at exactly the quote they already had, with no toast and no
  // page transition.
  builder.addCase(revalidateOwnerQuoteDetails().fulfilled, (state: InitialStateType, action) => {
    const previous = state.ownerQuote.quoteDetails;
    const next = formatResponseForQuoteSource(action.payload);

    state.ownerQuote.quoteDetails = next;
    // Publish the replaced snapshot alongside the new one. This is the only
    // place both versions exist; the form reads them once to reconcile its
    // selections, then clears this.
    state.ownerQuote.revalidation = {
      id: (state.ownerQuote.revalidation?.id ?? 0) + 1,
      previous,
    };
  });
  builder.addCase(revalidateOwnerQuoteDetails().rejected, () => {});

  // approve owner quote
  builder.addCase(ownerQuoteApprove().pending, (state: InitialStateType) => {
    state.ownerQuote.isApproving = true;
  });
  builder.addCase(ownerQuoteApprove().fulfilled, (state: InitialStateType, action) => {
    state.ownerQuote.isApproving = false;
    if (state?.ownerQuote?.quoteDetails?.quote_id === action?.payload?.quote_id) {
      state.ownerQuote.quoteDetails.approved_by = action?.meta?.arg?.approver_name ?? '';
      state.ownerQuote.quoteDetails.status = {
        ...(state?.ownerQuote?.quoteDetails?.status || {}),
        ...action.payload.status,
      };
      state.ownerQuote.quoteDetails.approved_at = action.payload.approved_at;
    }
  });
  builder.addCase(ownerQuoteApprove().rejected, (state: InitialStateType) => {
    state.ownerQuote.isApproving = false;
  });

  // coordinator quote approve
  builder.addCase(coordinatorQuoteApprove().fulfilled, (state: InitialStateType, action) => {
    const {
      workOrderId,
      quote_id: quoteId,
      status,
      approved_at: approvedAt,
      approved_by: approvedBy,
      abilities,
      quote_total_in_cents: quoteTotal,
      trip_fee_in_cents: tripFeeInCents,
      trip_fee_type: tripFeeType,
    } = action.payload;
    const argTasks: {
      quote_task_id: string;
      selected_option: string;
      task_type: string;
    }[] = action?.meta?.arg?.tasks || [];

    if (workOrderId && quoteId && state?.source?.[workOrderId]?.quotes?.quoteSource?.[quoteId]) {
      const quoteData = state.source?.[workOrderId]?.quotes?.quoteSource?.[quoteId];
      state.source[workOrderId].quotes.quoteSource[quoteId] = {
        ...(quoteData || {}),
        status: status || quoteData?.status,
        approved_at: approvedAt || '',
        approved_by: approvedBy || '',
        abilities: abilities || quoteData?.abilities,
        quote_total_in_cents: quoteTotal || 0,
        trip_fee_in_cents: tripFeeInCents,
        trip_fee_type: tripFeeType,
        tasks: quoteData.tasks.map((task) => {
          const index = argTasks?.findIndex(
            (argTask) => argTask.quote_task_id === task.quote_task_id,
          );
          if (index !== -1) {
            return { ...task, selected_option: argTasks[index]?.selected_option };
          }

          return task;
        }),
      };
    }
  });

  // send for customer approval
  builder.addCase(sendForCustomerApproval().fulfilled, (state: InitialStateType, action) => {
    const payload = action.payload as SendForCustomerApprovalResponse;
    const {
      workOrderId,
      quote_id: quoteId,
      status,
      abilities,
      customer_approval: customerApproval,
    } = payload;
    if (workOrderId && quoteId && state?.source?.[workOrderId]?.quotes?.quoteSource?.[quoteId]) {
      const quoteToUpdate = state.source[workOrderId].quotes.quoteSource[quoteId];
      quoteToUpdate.status = status || quoteToUpdate.status;
      quoteToUpdate.abilities = abilities || quoteToUpdate.abilities;
      if (customerApproval) {
        quoteToUpdate.customer_approval = customerApproval;
      }

      const approvalUrl =
        typeof payload.url === 'string' ? payload.url : customerApproval?.approval_url;
      if (typeof approvalUrl === 'string') {
        // Keep the existing flat key for compatibility while the authenticated
        // approval context becomes the durable source for coordinator reads.
        quoteToUpdate.public_url = approvalUrl;
        if (quoteToUpdate.customer_approval) {
          quoteToUpdate.customer_approval.approval_url = approvalUrl;
        }
      }
    }
  });

  // restore quote
  builder.addCase(restoreQuote().fulfilled, (state: InitialStateType, action) => {
    if (action?.payload?.quote_id && action?.payload?.workOrderId) {
      const woId = action?.payload?.workOrderId;
      const quoteId = action?.payload?.quote_id;
      state.source[woId].quotes.quoteSource[quoteId] = {
        ...(state?.source[woId]?.quotes?.quoteSource[quoteId] || {}),
        status: action?.payload?.status || {},
        abilities: action?.payload?.abilities || [],
      };
    }
  });

  builder.addCase(editQuote().pending, (state: InitialStateType) => {
    state.isCreatingQuote = true;
  });
  builder.addCase(editQuote().fulfilled, (state: InitialStateType, action) => {
    const woId = action?.payload?.workOrderId;
    if (action?.payload?.shouldUpdateSource) {
      if (state.source[woId]) {
        const quoteId = action?.payload?.quote_id;
        if (quoteId) {
          state.source[woId].quotes.quoteSource[quoteId] = {
            ...(state?.source?.[woId]?.quotes?.quoteSource?.[quoteId] || {}),
            ...(formatResponseForQuoteSource(action?.payload) || {}),
          };
        }
      }
    }
    state.isCreatingQuote = false;
  });
  builder.addCase(editQuote().rejected, (state: InitialStateType) => {
    state.isCreatingQuote = false;
  });
  builder.addCase(createQuoteTask().pending, (state: InitialStateType) => {
    state.isCreatingQuote = true;
  });
  builder.addCase(createQuoteTask().fulfilled, (state: InitialStateType, action) => {
    state.ownerQuote.quoteDetails = formatResponseForQuoteSource(action.payload);
    state.isCreatingQuote = false;
  });
  builder.addCase(createQuoteTask().rejected, (state: InitialStateType) => {
    state.isCreatingQuote = false;
  });

  // getIssueDetailsForCloseTrip
  builder.addCase(getIssueDetailsForCloseTrip().pending, (state: InitialStateType) => {
    state.isCloseTripIssueDetailsFetching = true;
  });
  builder.addCase(
    getIssueDetailsForCloseTrip().fulfilled,
    (state: InitialStateType, action: PayloadAction<GetIssueDetailsApiResType>) => {
      state.isCloseTripIssueDetailsFetching = false;
      state.currentScheduleDetails.closeTripData = {
        ...(state.currentScheduleDetails.closeTripData || {}),
        issues: {
          ...(state.currentScheduleDetails.closeTripData?.issues || {}),
          source: {
            ...(state.currentScheduleDetails.closeTripData?.issues?.source || {}),
            [action.payload.issue_id]: {
              ...(action.payload || {}),
              // trip_issue_status contains the status of trip issue
              status: action?.payload?.trip_issue_status,
              materials: resolveIssueMaterials(
                action?.payload?.materials,
                action?.payload?.quote_task,
              ).map((material) => {
                const materialObj: MaterialObjTypeForApi = {
                  material_id: material?.material_id,
                  label: material?.label,
                  quantity: material?.quantity,
                  quantity_type: material?.quantity_type,
                  cost_type: material?.cost_type,
                  cost_in_cents:
                    material?.cost_type === 'total'
                      ? material?.cost_in_cents
                      : material?.unit_price_in_cents,
                  markup_fee_type_value: material?.markup_fee_type_value || '',
                  markup_fee_type: material?.markup_fee_type || 'percentage',
                  total_cost_in_cents_including_markup: 0,
                  isUnSaved: false,
                };
                return {
                  ...materialObj,
                  total_cost_in_cents_including_markup:
                    calculateMaterialTotal(materialObj) * 100 || 0,
                };
              }),
            },
          },
        },
      };
    },
  );
  builder.addCase(getIssueDetailsForCloseTrip().rejected, (state: InitialStateType) => {
    state.isCloseTripIssueDetailsFetching = false;
  });

  builder.addCase(markAsDoneIssue().pending, (state: InitialStateType) => {
    state.isMarkingIssueAsDone = true;
  });
  builder.addCase(markAsDoneIssue().fulfilled, (state: InitialStateType, action) => {
    state.isMarkingIssueAsDone = false;
    if (action.payload.issue_id) {
      state.currentScheduleDetails.closeTripData = {
        ...(state.currentScheduleDetails.closeTripData || {}),
        issues: {
          ...(state.currentScheduleDetails.closeTripData?.issues || {}),
          source: {
            ...(state.currentScheduleDetails.closeTripData?.issues?.source || {}),
            [action.payload.issue_id]: action.payload,
          },
        },
      };
    }
  });
  builder.addCase(markAsDoneIssue().rejected, (state: InitialStateType) => {
    state.isMarkingIssueAsDone = false;
  });

  builder.addCase(getCloseTripInfo().pending, (state: InitialStateType) => {
    state.isFetchingCloseTripDetails = true;
  });
  builder.addCase(getCloseTripInfo().fulfilled, (state: InitialStateType, action) => {
    state.isFetchingCloseTripDetails = false;
    state.currentScheduleDetails.tripId = action?.payload?.trip_id;
    state.currentScheduleDetails.closeTripData = {
      quote_id: action?.payload.quote_id,
      appointment_type: action?.payload.appointment_type,
      is_approved_quote_trip: action?.payload?.is_approved_quote_trip,
      issues: (() => {
        type CloseTripIssuePayload = {
          work_order_issue_id: string;
          title: string;
          status: CloseTripIssueObjType['status'];
          created_at?: string;
        };
        const sortedIssues = sortByCreatedAt<CloseTripIssuePayload>(
          (action?.payload.work_order_issues || []) as CloseTripIssuePayload[],
        );
        return {
          ids: sortedIssues.map((issue) => issue.work_order_issue_id),
          source: sortedIssues.reduce<Record<string, CloseTripIssueObjType>>((acc, issue) => {
            acc[issue.work_order_issue_id] = {
              issue_id: issue.work_order_issue_id,
              title: issue.title,
              status: issue.status,
            };
            return acc;
          }, {}),
        };
      })(),
      cost_break_down: action?.payload?.cost_break_down,
    };
  });
  builder.addCase(getCloseTripInfo().rejected, (state: InitialStateType) => {
    state.isFetchingCloseTripDetails = false;
  });

  builder.addCase(updateDriveOrLaborTimeInCloseTrip().pending, (state: InitialStateType) => {
    state.isUpdatingDriveTimeOrLaborTimeOrVendorCost = true;
  });
  builder.addCase(
    updateDriveOrLaborTimeInCloseTrip().fulfilled,
    (state: InitialStateType, action) => {
      const type = action?.meta?.arg?.type;
      const payload = action?.payload || {};
      const {
        duration_in_sec: duration = 0,
        cost_in_cents: cost = 0,
        drive_time_adjusted_by: driveTimeAdjustedBy,
        labor_time_adjusted_by: labourTimeAdjustedBy,
      } = payload;

      const { closeTripData } = state.currentScheduleDetails;
      if (!closeTripData || !closeTripData.cost_break_down) {
        state.isUpdatingDriveTimeOrLaborTimeOrVendorCost = false;
        return;
      }

      const costBreakdown = closeTripData.cost_break_down;

      if (type === 'drive') {
        state.currentScheduleDetails.closeTripData!.cost_break_down = {
          ...costBreakdown,
          drive_time: {
            ...(costBreakdown?.drive_time || {}),
            duration_in_sec: duration,
            cost_in_cents: cost,
            drive_time_adjusted_by: driveTimeAdjustedBy,
          },
        };
      } else if (type === 'labor') {
        state.currentScheduleDetails.closeTripData!.cost_break_down = {
          ...costBreakdown,
          labor_time: {
            ...(costBreakdown?.labor_time || {}),
            duration_in_sec: duration,
            cost_in_cents: cost,
            labor_time_adjusted_by: labourTimeAdjustedBy,
          },
        };
      }
      state.isUpdatingDriveTimeOrLaborTimeOrVendorCost = false;
    },
  );
  builder.addCase(updateDriveOrLaborTimeInCloseTrip().rejected, (state: InitialStateType) => {
    state.isUpdatingDriveTimeOrLaborTimeOrVendorCost = false;
  });
  builder.addCase(updateQuoteTask().pending, (state: InitialStateType) => {
    state.isCreatingQuote = true;
  });
  builder.addCase(updateQuoteTask().fulfilled, (state: InitialStateType, action) => {
    state.ownerQuote.quoteDetails = formatResponseForQuoteSource(action.payload);
    state.isCreatingQuote = false;
  });
  builder.addCase(updateQuoteTask().rejected, (state: InitialStateType) => {
    state.isCreatingQuote = false;
  });

  builder.addCase(deleteWorkOrderMedia().pending, (state: InitialStateType) => {
    state.isDeletingWorkOrderMedia = true;
  });
  builder.addCase(deleteWorkOrderMedia().fulfilled, (state: InitialStateType, action) => {
    state.ownerQuote.quoteDetails = formatResponseForQuoteSource(action.payload);
    state.isDeletingWorkOrderMedia = false;
  });
  builder.addCase(deleteWorkOrderMedia().rejected, (state: InitialStateType) => {
    state.isDeletingWorkOrderMedia = false;
  });
  // Mark as pending
  builder.addCase(markAsPendingIssue().fulfilled, (state: InitialStateType, action) => {
    if (action.payload.issue_id) {
      state.currentScheduleDetails.closeTripData = {
        ...(state.currentScheduleDetails.closeTripData || {}),
        issues: {
          ...(state.currentScheduleDetails.closeTripData?.issues || {}),
          source: {
            ...(state.currentScheduleDetails.closeTripData?.issues?.source || {}),
            [action.payload.issue_id]: action.payload,
          },
        },
      };
    }
  });

  builder.addCase(closeTrip().pending, (state: InitialStateType) => {
    state.isClosingTrip = true;
  });
  builder.addCase(closeTrip().fulfilled, (state: InitialStateType, action) => {
    const payload = action?.payload;
    state.source[payload?.work_order_id] = {
      ...(state.source[payload?.work_order_id] || {}),
      status: payload?.status || {},
      abilities: payload?.abilities,
      trips: {
        ...(state?.source?.[payload?.work_order_id]?.trips || {}),
        tripSource: {
          ...(state?.source?.[payload?.work_order_id]?.trips?.tripSource || {}),
          [payload?.trip?.trip_id]: {
            ...(state?.source?.[payload?.work_order_id]?.trips?.tripSource?.[
              payload?.trip?.trip_id
            ] || {}),
            status: payload?.trip?.status,
          },
        },
      },
    };

    state.isClosingTrip = false;
  });
  builder.addCase(closeTrip().rejected, (state: InitialStateType) => {
    state.isClosingTrip = false;
  });
  builder.addCase(updateVendorLaborCloseTrip().pending, (state: InitialStateType) => {
    state.isUpdatingDriveTimeOrLaborTimeOrVendorCost = true;
  });
  builder.addCase(updateVendorLaborCloseTrip().fulfilled, (state: InitialStateType, action) => {
    const { closeTripData } = state.currentScheduleDetails;
    if (!closeTripData || !closeTripData.cost_break_down) {
      state.isUpdatingDriveTimeOrLaborTimeOrVendorCost = false;
      return;
    }

    const costBreakdown = closeTripData?.cost_break_down;
    state.currentScheduleDetails.closeTripData!.cost_break_down = {
      ...(costBreakdown || {}),
      labor: action?.payload?.labor_cost_in_cents,
    };
    state.isUpdatingDriveTimeOrLaborTimeOrVendorCost = false;
  });
  builder.addCase(updateVendorLaborCloseTrip().rejected, (state: InitialStateType) => {
    state.isUpdatingDriveTimeOrLaborTimeOrVendorCost = false;
  });

  builder.addCase(dispatchToLula().pending, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = true;
  });
  builder.addCase(dispatchToLula().fulfilled, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });
  builder.addCase(dispatchToLula().rejected, (state: InitialStateType) => {
    state.isSchedulingWorkOrder = false;
  });

  builder.addCase(getServiceRequestDetails().fulfilled, (state: InitialStateType, action) => {
    action?.payload?.work_orders?.forEach((work_order: any) => {
      const workOrderId = work_order?.work_order_id;
      const existingWorkOrder = state?.source?.[workOrderId];
      const mergedWorkOrder = {
        ...existingWorkOrder,
        ...work_order,
      };
      const hasNestedNteAmount =
        work_order?.nte_amount !== undefined && work_order?.nte_amount !== null;
      const hasFlatNteAmount =
        work_order?.nte_amount_in_cents !== undefined && work_order?.nte_amount_in_cents !== null;

      if (hasNestedNteAmount || hasFlatNteAmount) {
        const mergedNteAmount: any = existingWorkOrder?.nte_amount
          ? { ...existingWorkOrder.nte_amount }
          : {};

        if (hasNestedNteAmount) {
          Object.assign(mergedNteAmount, work_order?.nte_amount);
        }

        if (hasFlatNteAmount) {
          mergedNteAmount.nte_amount_in_cents = work_order?.nte_amount_in_cents;
        }

        mergedWorkOrder.nte_amount =
          Object.keys(mergedNteAmount).length > 0 ? mergedNteAmount : work_order?.nte_amount;
      } else if (existingWorkOrder?.nte_amount) {
        mergedWorkOrder.nte_amount = existingWorkOrder.nte_amount;
      }

      state.source[workOrderId] = mergedWorkOrder;
    });
    state.isFetchingWorkOrderDetails = false;
  });

  builder.addCase(getWorkOrdersForGroup().fulfilled, (state: InitialStateType, action) => {
    const groupKey = action.payload?._group_key as string | undefined;
    if (!groupKey) return;
    const items: WorkOrderAPIResponseObj[] = action.payload?.data ?? [];
    const meta = action.payload?.meta;

    // Normalize each group item exactly like the flat listing so tags, trips,
    // quotes, etc. render consistently. Storing the raw response left `tags` as
    // objects (breaking the tag column, which resolves ids via `tagSource`) and
    // produced malformed attach payloads.
    // Which WO keeps its rich detail record is taken from the thunk arg captured
    // at dispatch time — NOT by reading `window` at completion time. A grouped
    // page fetch that resolves after the panel has switched must still protect
    // the WO that was open when it was dispatched (COM-715). Falls back to the
    // open panel for callers that don't pass an id.
    const expandedWorkOrderId =
      action?.meta?.arg?.expandedId || state?.showSidePanel?.workOrderUuid || '';
    const newTagSource = { ...(state?.tagSource || {}) };

    items.forEach((workOrder) => {
      if (
        workOrder?.work_order_id !== expandedWorkOrderId ||
        isEmpty(state?.source?.[expandedWorkOrderId])
      ) {
        state.source[workOrder.work_order_id] = normalizeWorkOrderListItem(
          workOrder,
          newTagSource,
          state?.source?.[workOrder.work_order_id],
        );
      }
    });

    state.tagSource = newTagSource;

    const newIds = items.map((workOrder) => workOrder.work_order_id);
    applyGroupedDataPage(
      state,
      groupKey,
      newIds,
      meta,
      action.payload?._attempt as number | undefined,
    );
  });

  builder.addCase(getWorkOrdersForGroup().rejected, (state: InitialStateType, action) => {
    applyGroupFetchRejected(state, action);
  });

  // Unified Quote Rejection Handling using addMatcher
  builder
    // Handle pending state for both owner and coordinator quote rejections
    .addMatcher(
      isAnyOf(ownerQuoteReject().pending, coordinatorQuoteReject().pending),
      (state: InitialStateType) => {
        state.isRejectingQuote = true;
      },
    )
    // Handle successful rejection for both quote types
    .addMatcher(
      isAnyOf(ownerQuoteReject().fulfilled, coordinatorQuoteReject().fulfilled),
      (state: InitialStateType, action: any) => {
        // Unified quote rejection handler for both owner and coordinator flows
        state.isRejectingQuote = false;
        const reqPayload = action?.meta?.arg;
        const woId = action?.payload?.workOrderId;
        const quoteId = action?.payload?.quote_id;
        const isOwnerFlow = action.type.includes('ownerQuoteReject');

        const rejectionData = {
          rejected_by: action.payload?.rejected_by ?? '',
          status: action.payload.status,
          rejected_at: action.payload.rejected_date || action.payload.rejected_at || '',
          abilities: action.payload.abilities ?? [],
          reject_type: reqPayload?.reject_type ?? '',
          reject_expected_price: reqPayload?.expected_price ?? '',
          rejected_note: reqPayload?.note ?? '',
          trip_fee_in_cents: action?.payload?.trip_fee_in_cents,
          trip_fee_type: action?.payload?.trip_fee_type,
        };

        // Update owner quote details if it's owner flow
        if (isOwnerFlow && state?.ownerQuote?.quoteDetails?.quote_id === quoteId) {
          state.ownerQuote.quoteDetails.rejected_by = rejectionData.rejected_by;
          state.ownerQuote.quoteDetails.reject_type = rejectionData.reject_type;
          state.ownerQuote.quoteDetails.reject_expected_price = rejectionData.reject_expected_price;
          state.ownerQuote.quoteDetails.status = {
            ...(state?.ownerQuote?.quoteDetails?.status || {}),
            ...(rejectionData.status || {}),
          };
          state.ownerQuote.quoteDetails.rejected_at = rejectionData.rejected_at;
        }

        // Always update main quotes collection (single source of truth)
        if (woId && state.source[woId] && state.source[woId].quotes?.quoteSource?.[quoteId]) {
          state.source[woId].quotes.quoteSource[quoteId] = {
            ...(state.source[woId].quotes.quoteSource[quoteId] || {}),
            ...rejectionData,
            tasks: (state.source[woId].quotes.quoteSource[quoteId].tasks || [])?.map((task) => ({
              ...(task || {}),
              selected_option: '',
            })),
          };
        }
      },
    )
    // Handle rejection errors for both quote types
    .addMatcher(
      isAnyOf(ownerQuoteReject().rejected, coordinatorQuoteReject().rejected),
      (state: InitialStateType) => {
        state.isRejectingQuote = false;
      },
    );
};
export default extraReducers;
